import { useEffect, useMemo, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { AppIcon, type AppIconName } from '../components/AppIcon';
import { BrandLogo } from '../components/BrandLogo';
import { env } from '../lib/env';
import { getSupabaseClient } from '../lib/supabase';
import { getSupabaseRuntimeIdentity } from '../features/runtime/supabaseAdapter';
import type { RuntimeActorRole, RuntimeIdentityV1 } from '../features/runtime/types';

export type NavItem = { to: string; label: string; icon?: AppIconName };

type Props = {
  nav: NavItem[];
  roleLabel: string;
  allowedRoles?: RuntimeActorRole[];
};

function fallbackIcon(label: string): AppIconName {
  const key = label.toLowerCase();
  if (key.includes('beranda') || key.includes('dashboard')) return 'home';
  if (key.includes('belajar') || key.includes('pelajaran')) return 'book';
  if (key.includes('progress')) return 'chart';
  if (key.includes('listen')) return 'headphones';
  if (key.includes('murid') || key.includes('anak')) return 'users';
  if (key.includes('scan')) return 'scan';
  if (key.includes('laporan')) return 'report';
  if (key.includes('pr') || key.includes('assignment')) return 'clipboard';
  if (key.includes('bantuan')) return 'help';
  return 'pencil';
}

export function RoleShell({ nav, roleLabel, allowedRoles = [] }: Props) {
  const client = useMemo(() => getSupabaseClient(), []);
  const navigate = useNavigate();
  const location = useLocation();
  const [identity, setIdentity] = useState<RuntimeIdentityV1 | null>(null);
  const [checking, setChecking] = useState(Boolean(env.supabaseUrl && env.supabasePublishableKey));
  const [accessError, setAccessError] = useState('');

  useEffect(() => {
    if (!env.supabaseUrl || !env.supabasePublishableKey || !client) {
      setChecking(false);
      return;
    }
    let cancelled = false;
    void (async () => {
      const session = await client.auth.getSession();
      if (cancelled) return;
      if (!session.data.session) {
        navigate(`/login?next=${encodeURIComponent(location.pathname + location.search)}`, { replace: true });
        return;
      }
      try {
        const current = await getSupabaseRuntimeIdentity();
        if (cancelled) return;
        setIdentity(current);
        if (allowedRoles.length > 0 && !current?.roles.some((item) => allowedRoles.includes(item.role))) {
          setAccessError(`Akun ini tidak mempunyai akses ${roleLabel}.`);
        }
      } catch (error) {
        if (!cancelled) setAccessError(error instanceof Error ? error.message : 'Identity tidak dapat diverifikasi.');
      } finally {
        if (!cancelled) setChecking(false);
      }
    })();
    return () => { cancelled = true; };
  }, [allowedRoles, client, location.pathname, location.search, navigate, roleLabel]);

  async function logout() {
    if (client) await client.auth.signOut();
    navigate('/login', { replace: true });
  }

  if (checking) {
    return (
      <main className="auth-state">
        <BrandLogo className="auth-state__logo" />
        <section className="card">Memverifikasi akun…</section>
      </main>
    );
  }

  if (accessError) {
    return (
      <main className="auth-state">
        <section className="feedback feedback--error">
          <strong>Akses ditolak.</strong><p>{accessError}</p>
        </section>
      </main>
    );
  }

  const displayName = roleLabel === 'Murid' ? (identity?.student?.displayName || identity?.displayName || 'Murid BelajarKu') : (identity?.displayName || roleLabel);
  const shortRole = roleLabel === 'Guru' ? 'Tutor / Guru' : roleLabel;
  const mobileNav = nav.slice(0, 5);

  return (
    <div className="production-shell">
      <aside className="production-sidebar no-print">
        <div className="production-sidebar__brand"><BrandLogo variant="sidebar" /></div>
        <nav className="production-sidebar__nav" aria-label={`Navigasi ${roleLabel}`}>
          {nav.map((item) => (
            <NavLink key={item.to} to={item.to} end={item.to.split('/').filter(Boolean).length === 1}>
              <AppIcon name={item.icon || fallbackIcon(item.label)} />
              <span>{item.label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="production-sidebar__footer">
          <div className="sidebar-safety"><AppIcon name="shield" /><span>Riwayat belajarmu tersimpan aman.</span></div>
          {client && env.supabaseUrl && env.supabasePublishableKey ? (
            <button className="sidebar-logout" type="button" onClick={() => void logout()}>
              <AppIcon name="logout" /> Keluar
            </button>
          ) : null}
        </div>
      </aside>

      <div className="production-workspace">
        <header className="production-topbar no-print">
          <div className="production-topbar__mobile-brand"><BrandLogo variant="header" /></div>
          <div className="production-topbar__context">
            <span>{shortRole}</span>
            <strong>{displayName}</strong>
          </div>
          <div className="production-topbar__actions">
            {roleLabel === 'Murid' ? (
              <NavLink className="top-search" to="/student/subjects" aria-label="Buka daftar pelajaran"><AppIcon name="search" /><span>Cari pelajaran atau topik</span></NavLink>
            ) : (
              <div className="top-search" aria-hidden="true"><AppIcon name="search" /><span>Cari data</span></div>
            )}
            <button className="icon-button" type="button" aria-label="Notifikasi belum tersedia" title="Notifikasi akan diaktifkan pada tahap berikutnya" disabled><AppIcon name="bell" /></button>
            <div className="user-chip" title={`${shortRole} · ${displayName}`}>
              <span className="user-chip__avatar">{roleLabel === 'Murid' ? '👧' : roleLabel === 'Orang Tua' ? '👩' : roleLabel === 'Guru' ? '👨‍🏫' : '👤'}</span>
              <span className="user-chip__copy"><b>{displayName}</b><small>{shortRole}</small></span>
            </div>
          </div>
        </header>

        <main className="production-main"><Outlet /></main>

        <nav className="mobile-bottom-nav no-print" aria-label={`Navigasi mobile ${roleLabel}`}>
          {mobileNav.map((item) => (
            <NavLink key={item.to} to={item.to} end={item.to.split('/').filter(Boolean).length === 1}>
              <AppIcon name={item.icon || fallbackIcon(item.label)} />
              <span>{item.label}</span>
            </NavLink>
          ))}
        </nav>
      </div>
    </div>
  );
}
